import type {
  Category,
  CategoryInput,
  Item,
  ItemInput,
  ItemQuery,
  Page,
  StockLevel,
  StockMovement,
  Supplier,
  SupplierInput,
  SupplierQuery,
} from '@iobiz/shared'
import { api, apiFetch } from '../../api/client.js'

/** Accès au catalogue. Basculera sur la réplique locale au Sprint 4. */

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== null) search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

// ------------------------------------------------------------------ articles

export const listItems = (storeId: string, q: Partial<ItemQuery>, signal?: AbortSignal) =>
  api.get<Page<Item>>(`/stores/${storeId}/items${qs(q)}`, signal)

export const getItem = (storeId: string, id: string) =>
  api.get<Item>(`/stores/${storeId}/items/${id}`)

export const createItem = (storeId: string, input: ItemInput) =>
  api.post<Item>(`/stores/${storeId}/items`, input)

export const updateItem = (storeId: string, id: string, input: ItemInput, version: number) =>
  apiFetch<Item>(`/stores/${storeId}/items/${id}`, {
    method: 'PATCH',
    body: input,
    headers: { 'If-Match': `"${version}"` },
  })

export const deleteItem = (storeId: string, id: string) =>
  api.delete<void>(`/stores/${storeId}/items/${id}`)

// ---------------------------------------------------------------- catégories

export const listCategories = (storeId: string) =>
  api.get<{ data: Category[] }>(`/stores/${storeId}/categories`)

export const createCategory = (storeId: string, input: CategoryInput) =>
  api.post<{ id: string }>(`/stores/${storeId}/categories`, input)

export const updateCategory = (storeId: string, id: string, input: CategoryInput) =>
  api.patch<void>(`/stores/${storeId}/categories/${id}`, input)

export const deleteCategory = (storeId: string, id: string) =>
  api.delete<void>(`/stores/${storeId}/categories/${id}`)

/** Aplatit l'arbre pour alimenter un menu déroulant, en indentant les niveaux. */
export function flattenCategories(
  tree: Category[],
  depth = 0,
): { id: string; label: string; depth: number; kind: Category['kind'] }[] {
  return tree.flatMap((c) => [
    {
      id: c.id,
      label: `${'  '.repeat(depth)}${depth > 0 ? '└ ' : ''}${c.name}`,
      depth,
      // ⭐ Le type suit la catégorie : la saisie d'un article ne doit proposer
      // que les catégories de sa famille, et une sous-catégorie hérite de son
      // parent. Sans lui, les deux règles seraient impossibles à tenir.
      kind: c.kind,
    },
    ...flattenCategories(c.children, depth + 1),
  ])
}

// -------------------------------------------------------------- fournisseurs

export const listSuppliers = (storeId: string, q: Partial<SupplierQuery>, signal?: AbortSignal) =>
  api.get<Page<Supplier>>(`/stores/${storeId}/suppliers${qs(q)}`, signal)

export const getSupplier = (storeId: string, id: string) =>
  api.get<Supplier>(`/stores/${storeId}/suppliers/${id}`)

export const createSupplier = (storeId: string, input: SupplierInput) =>
  api.post<Supplier>(`/stores/${storeId}/suppliers`, input)

export const updateSupplier = (
  storeId: string,
  id: string,
  input: SupplierInput,
  version: number,
) =>
  apiFetch<Supplier>(`/stores/${storeId}/suppliers/${id}`, {
    method: 'PATCH',
    body: input,
    headers: { 'If-Match': `"${version}"` },
  })

export const deleteSupplier = (storeId: string, id: string) =>
  api.delete<void>(`/stores/${storeId}/suppliers/${id}`)

// -------------------------------------------------------------------- stock

/**
 * Le stock passe par le RÉSEAU, pas par la réplique.
 *
 * Seuls les niveaux agrégés descendent sur les appareils (§7.5) ; l'historique
 * des mouvements reste serveur. Consulter l'historique suppose donc une
 * connexion — c'est assumé : on ne le regarde pas au comptoir, on le regarde
 * quand on cherche à comprendre un écart.
 */
export const getStock = (storeId: string, itemId: string) =>
  api.get<{ level: StockLevel; movements: StockMovement[] }>(
    `/stores/${storeId}/items/${itemId}/stock`,
  )

export const recordMovement = (storeId: string, itemId: string, input: unknown) =>
  api.post<{ movement: StockMovement; level: StockLevel }>(
    `/stores/${storeId}/items/${itemId}/stock/movements`,
    input,
  )

export const getStockAlerts = (storeId: string) =>
  api.get<{ data: { itemId: string; name: string; quantity: number; minStockLevel: number }[] }>(
    `/stores/${storeId}/stock/alerts`,
  )
